// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { miniCartLineStyles } from "@conklin/App/Components/MiniCart/MiniCartLineCard";
import { GridOffset } from "@conklin/MobiusOverrides/GridContainer/GridContainer";
import { ProductPriceStyles } from "@conklin/Overrides/Components/ProductPrice";
import IconCheckmark from "@conklin/XDL/Icons/IconCheckmark";
import IconClose from "@conklin/XDL/Icons/IconClose";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import { xScreen } from "@conklin/XDL/Utilities";
import color from "@conklin/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@conklin/XDL/Utilities/links";
import { IconWrapper } from "@insite/mobius/Icon";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { css } from "styled-components";

const priceStyle: TypographyPresentationProps = {
    size: 16,
    weight: fontConfig.weight.bold,
    css: css`
        letter-spacing: 0;
        margin-right: 4px;
        &&& {
            line-height: 19px;
        }
    `,
};

miniCartLineStyles.cartLine = {
    css: css`
        display: flex;
        position: relative;
        margin-bottom: 12px;
        padding-bottom: 12px;
        width: 440px;
        max-width: 100%;
        &:not(:last-of-type) {
            border-bottom: 1px solid ${color("common.border")};
        }
    `,
};

miniCartLineStyles.imageWrapper = {
    css: css`
        width: 91px;
        height: 91px;
        overflow: hidden;
        margin-right: 16px;
        flex-shrink: 0;
    `,
};

miniCartLineStyles.cartLineImage = {
    width: "91",
    height: "91",
    css: css`
        max-height: 100%;
        max-width: 100%;
        height: auto;
        object-fit: contain;
    `,
};

miniCartLineStyles.detailsWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        max-width: 303px;
        ${xScreen("lte", "md")} {
            width: 100%;
            max-width: initial;
        }
    `,
};

miniCartLineStyles.partNumbersStyles = {
    container: {
        gap: 0,
        css: css`
            width: 100%;

            ${GridOffset} {
                flex-direction: column;
            }
        `,
    },
};

miniCartLineStyles.qtyAndPriceWrapper = {
    css: css`
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: space-between;
        margin-top: 8px;
    `,
};

miniCartLineStyles.basicProductPriceStyles = {
    wrapper: {
        css: css`
            width: auto;
        `,
    },
    price: {
        unitOfMeasure: {
            unitOfMeasureText: {
                weight: fontConfig.weight.regular,
                size: 12,
                css: css`
                    letter-spacing: 0;
                    line-height: 15px;
                `,
            },
        },
        priceText: priceStyle,
        errorText: priceStyle,
        invalidPriceText: priceStyle,
    },
    invalidPriceText: priceStyle,
    quoteMessage: {
        text: priceStyle,
    },
} as ProductPriceStyles;

miniCartLineStyles.removeCartLineLink = {
    icon: {
        iconProps: {
            src: IconClose,
            size: 12,
            color: "common.ui5",
        },
    },
    css: css`
        margin-left: auto;
        position: absolute;
        right: 0;
        top: 0;
        ${IconWrapper} {
            margin-right: 0;
            &,
            & svg {
                height: auto;
            }
        }

        &:hover {
            &,
            & svg {
                color: ${color("common.ui10")};
            }
        }
    `,
};

miniCartLineStyles.errorMessagesWrapper = {
    css: css`
        width: 100%;
        margin-top: 10px;
        & > div:not(:last-child) {
            margin-bottom: 12px;
        }
    `,
};

miniCartLineStyles.removeConfirmation = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        height: 0;
        margin-bottom: 0;
        transition: all 0.3s ease-in-out;
        overflow: hidden;
        opacity: 0;
        &[aria-expanded="true"] {
            height: 38px;
            border-top: 1px solid ${color("common.border")};
            margin-top: 10px;
            margin-bottom: -14px;
            opacity: 1;
        }
    `,
};

miniCartLineStyles.removeConfirmationText = {
    size: 12,
    color: "text.main",
    weight: fontConfig.weight.bold,
};

miniCartLineStyles.removeConfirmationConfirm = {
    ...iconButtonCommonStyles(IconCheckmark, 10),
};

miniCartLineStyles.removeConfirmationClose = {
    ...iconButtonCommonStyles(IconClose, 10),
};
